import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import DocumentIcon from '@mui/icons-material/Description';
import { useAppContext } from '../../context/AppContext';

const DocumentList = () => {
  const {
    topics,
    documents,
    loading,
    error,
    selectedTopic,
    setSelectedTopic,
    fetchDocumentsByTopic,
  } = useAppContext();

  // Add local error state to handle API errors without affecting the app context
  const [localError, setLocalError] = useState(null);

  // Fetch documents when selected topic changes
  useEffect(() => {
    let isMounted = true;
    
    const loadDocuments = async () => {
      if (!selectedTopic) return;
      
      setLocalError(null);
      try {
        // Check if we already have documents for this topic to avoid unnecessary fetches
        if (documents[selectedTopic] && documents[selectedTopic].length > 0) {
          return; // Already have documents, no need to fetch again
        }
        
        await fetchDocumentsByTopic(selectedTopic);
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching documents:", err);
          setLocalError(`Failed to fetch documents for ${selectedTopic}: ${err.message}`);
        }
      }
    };
    
    loadDocuments();
    
    return () => {
      isMounted = false;
    };
  }, [selectedTopic]);

  const handleTopicChange = (event) => {
    setSelectedTopic(event.target.value);
  };

  // Safely access documents with fallbacks
  const currentDocuments = selectedTopic && documents ? 
    (documents[selectedTopic] || []) : [];

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Document Library
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {(error || localError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || localError}
        </Alert>
      )}

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="topic-select-label">Select Topic</InputLabel>
        <Select
          labelId="topic-select-label"
          value={selectedTopic || ''}
          label="Select Topic"
          onChange={handleTopicChange}
          disabled={loading || topics.length === 0}
        >
          {topics.map((topic) => (
            <MenuItem key={topic} value={topic}>
              {topic}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : selectedTopic ? (
        Array.isArray(currentDocuments) && currentDocuments.length > 0 ? (
          <List>
            {currentDocuments.map((doc, index) => (
              <React.Fragment key={doc.document_id || `doc-${index}`}>
                <ListItem alignItems="flex-start">
                  <ListItemIcon>
                    <DocumentIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.document_id || `Document ${index + 1}`}
                    secondary={
                      <React.Fragment>
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                        >
                          Source: {doc.original_source || 'Unknown'}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label={`Topic: ${doc.topic_id || selectedTopic}`}
                            size="small"
                            color="primary"
                            sx={{ mr: 1 }}
                          />
                        </Box>
                      </React.Fragment>
                    }
                  />
                </ListItem>
                {index < currentDocuments.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Alert severity="info">
            No documents found for this topic. Upload some documents to get started.
          </Alert>
        )
      ) : (
        <Alert severity="info">
          Select a topic to view its documents.
        </Alert>
      )}
    </Paper>
  );
};

export default DocumentList;


